
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

// Updated Job interface to match the one in CompanyOffersTab
export interface Job {
  id: string;
  title: string;
  company_id: string;
  company_name?: string;
  description?: string | null;
  candidateCount?: number;
  contact_id?: string | null;
  created_at: string;
  skills?: string[];
  status?: string;
}

export const useCompanyJobsList = (companyId: string | undefined) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list');
  
  const fetchJobs = useCallback(async () => {
    if (!companyId) return;
    
    try {
      setJobsLoading(true);
      
      const { data, error } = await supabase
        .from('jobs')
        .select('id, title, description, created_at, company_id, skills, contact_id, companies(name)')
        .eq('company_id', companyId);
      
      if (error) throw error;
      
      if (data) {
        // Get candidate count for each job
        const jobsWithCounts = await Promise.all(data.map(async (job) => {
          const { count, error: countError } = await supabase
            .from('matches')
            .select('id', { count: 'exact', head: true })
            .eq('job_id', job.id);
          
          return {
            ...job,
            candidateCount: countError ? 0 : count || 0,
            company_name: job.companies?.name || 'Unknown',
            skills: job.skills || []
          };
        }));
        
        setJobs(jobsWithCounts);
      }
    } catch (error) {
      console.error("Error fetching company jobs:", error);
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  }, [companyId]);
  
  useEffect(() => {
    fetchJobs();
  }, [companyId, fetchJobs]);
  
  // Filter jobs by search term
  const filteredJobs = jobs.filter(job => {
    if (!searchTerm) return true;
    
    const term = searchTerm.toLowerCase();
    return (
      job.title.toLowerCase().includes(term) ||
      (job.description && job.description.toLowerCase().includes(term))
    );
  });
  
  return {
    jobs: filteredJobs,
    jobsLoading,
    searchTerm,
    setSearchTerm,
    viewMode,
    setViewMode,
    refreshJobs: fetchJobs
  };
};
